import { useState } from 'react';
import { AlertCircle, ShieldAlert } from 'lucide-react';
import {
  type ActivityEventSummary,
  type ActivityLogFilters,
  useActivityEvent,
  useActivityLog,
} from '@/hooks/useActivityLog';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/**
 * The Activity Log — who did what, to which resource, with what outcome.
 *
 * Deliberately plain. For a pre-sales product "here is every privileged action with
 * its actor" *is* the demo; a chart of it is not.
 */

const CATEGORIES = [
  'access_control',
  'credentials',
  'platform_admin',
  'device_ops',
  'change_mgmt',
  'monitoring_config',
  'billing',
  'tenancy',
  'sessions',
  'identity',
  'unknown',
] as const;

const OUTCOMES = ['success', 'denied', 'rejected', 'failed'] as const;

/** Human-readable category, without shouting the snake_case at the user. */
function humanize(value: string): string {
  return value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/**
 * `denied` gets its own visual weight rather than sharing "not success" with
 * validation errors — an attempt that was refused is the row worth noticing.
 */
function OutcomeBadge({ outcome }: { outcome: ActivityEventSummary['outcome'] }) {
  if (outcome === 'denied') {
    return (
      <Badge variant="destructive" className="gap-1">
        <ShieldAlert className="w-3 h-3" />
        Denied
      </Badge>
    );
  }
  if (outcome === 'success') return <Badge variant="secondary">Success</Badge>;
  if (outcome === 'failed') return <Badge variant="destructive">Failed</Badge>;
  return <Badge variant="outline">Rejected</Badge>;
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 py-1.5 border-b last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm break-all">{children}</span>
    </div>
  );
}

function EventDetail({ id }: { id: string }) {
  const { data, isLoading, error } = useActivityEvent(id);

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Could not load this event.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <DetailRow label="Action">
          <code>{data.action}</code>
        </DetailRow>
        <DetailRow label="Outcome">
          <OutcomeBadge outcome={data.outcome} /> <span className="text-muted-foreground">HTTP {data.httpStatus}</span>
        </DetailRow>
        <DetailRow label="When">{new Date(data.occurredAt).toLocaleString()}</DetailRow>
        <DetailRow label="Actor">
          {data.actorLabel}
          {data.viaAdmin ? <Badge variant="destructive" className="ml-2">via admin</Badge> : null}
        </DetailRow>
        {data.targetLabel || data.targetType ? (
          <DetailRow label="Target">
            {data.targetLabel ?? data.targetId} {data.targetType ? <span className="text-muted-foreground">({data.targetType})</span> : null}
          </DetailRow>
        ) : null}
        {data.reason ? <DetailRow label="Reason">{data.reason}</DetailRow> : null}
        <DetailRow label="Credential">{data.credentialKind ?? '—'}</DetailRow>
        <DetailRow label="Source IP">{data.sourceIp ?? '—'}</DetailRow>
        <DetailRow label="User agent">{data.userAgent ?? '—'}</DetailRow>
      </div>

      {data.beforeState || data.afterState ? (
        <div>
          <h3 className="text-sm font-medium mb-2">Change</h3>
          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <span className="text-xs text-muted-foreground">Before</span>
              <pre className="mt-1 text-xs bg-muted rounded p-2 overflow-x-auto">
                {JSON.stringify(data.beforeState ?? null, null, 2)}
              </pre>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">After</span>
              <pre className="mt-1 text-xs bg-muted rounded p-2 overflow-x-auto">
                {JSON.stringify(data.afterState ?? null, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      ) : null}

      {data.details ? (
        <div>
          <h3 className="text-sm font-medium mb-2">Details</h3>
          <pre className="text-xs bg-muted rounded p-2 overflow-x-auto">
            {JSON.stringify(data.details, null, 2)}
          </pre>
        </div>
      ) : null}

      <div>
        <h3 className="text-sm font-medium mb-2">Correlation</h3>
        {/* The trace joins this to other services; the span is this request alone. */}
        <DetailRow label="Trace">
          <code className="text-xs">{data.traceId}</code>
        </DetailRow>
        <DetailRow label="Span">
          <code className="text-xs">{data.spanId}</code>
        </DetailRow>
      </div>
    </div>
  );
}

export function ActivityLogPage() {
  const [filters, setFilters] = useState<ActivityLogFilters>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useActivityLog(filters);

  const events = data?.pages.flatMap((p) => p.events) ?? [];

  const columns: readonly ResponsiveTableColumn<ActivityEventSummary>[] = [
    {
      key: 'occurredAt',
      label: 'When',
      render: (e) => (
        <span className="whitespace-nowrap text-sm">{new Date(e.occurredAt).toLocaleString()}</span>
      ),
    },
    {
      key: 'actor',
      label: 'Actor',
      hideMobileLabel: true,
      render: (e) => (
        <span className="font-medium">
          {e.actorLabel}
          {e.viaAdmin ? <Badge variant="destructive" className="ml-2">admin</Badge> : null}
        </span>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (e) => <code className="text-xs">{e.action}</code>,
    },
    {
      key: 'target',
      label: 'Target',
      hideAt: 'mobile',
      render: (e) => e.targetLabel ?? (e.targetType ? <span className="text-muted-foreground">{e.targetType}</span> : '—'),
    },
    {
      key: 'outcome',
      label: 'Outcome',
      render: (e) => <OutcomeBadge outcome={e.outcome} />,
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Activity Log"
        subtitle="Every privileged action taken in this organization — who did it, to what, and how it ended."
      />

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="grid gap-1.5">
          <Label htmlFor="activity-category" className="text-xs">Category</Label>
          <Select
            value={filters.category ?? 'all'}
            onValueChange={(v) => { setFilters((f) => ({ ...f, category: v === 'all' ? undefined : v })); }}
          >
            <SelectTrigger id="activity-category" className="w-48">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{humanize(c)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="activity-outcome" className="text-xs">Outcome</Label>
          <Select
            value={filters.outcome ?? 'all'}
            onValueChange={(v) => { setFilters((f) => ({ ...f, outcome: v === 'all' ? undefined : v })); }}
          >
            <SelectTrigger id="activity-outcome" className="w-40">
              <SelectValue placeholder="Any outcome" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any outcome</SelectItem>
              {OUTCOMES.map((o) => (
                <SelectItem key={o} value={o}>{humanize(o)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-1.5">
          {/* A trailing colon matches every verb on a resource — `role:` finds
              role:create, role:delete and role:add_permission alike. */}
          <Label htmlFor="activity-action" className="text-xs">Action</Label>
          <Input
            id="activity-action"
            className="w-56"
            placeholder="role: or role:create"
            value={filters.action ?? ''}
            onChange={(e) => { setFilters((f) => ({ ...f, action: e.target.value || undefined })); }}
          />
        </div>

        {Object.keys(filters).length > 0 ? (
          <Button variant="ghost" onClick={() => { setFilters({}); }}>Clear</Button>
        ) : null}
      </div>

      {error ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Could not load the activity log.</AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : events.length === 0 ? (
        <EmptyState
          title="Nothing recorded yet"
          description="Privileged actions — permission changes, credential edits, administrative operations — appear here as they happen."
        />
      ) : (
        <>
          <ResponsiveTable
            columns={columns}
            rows={events}
            rowKey={(e) => e.id}
            onRowClick={(e) => { setSelectedId(e.id); }}
          />
          {hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => { void fetchNextPage(); }}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <AppSheet
        open={selectedId !== null}
        onOpenChange={(open) => { if (!open) setSelectedId(null); }}
        title="Activity detail"
        size="lg"
      >
        {selectedId ? <EventDetail id={selectedId} /> : null}
      </AppSheet>
    </PageContainer>
  );
}

export default ActivityLogPage;
